<script setup lang="ts">
const { t } = useI18n()

interface DateRange {
  from?: string
  to?: string
}

const props = withDefaults(
  defineProps<{
    ages?: number[]
    allContestGames?: boolean
    allFrequencyTypes?: boolean
    allGenders?: boolean
    allSegments?: boolean
    allStores?: boolean
    allSurveys?: boolean
    allTags?: boolean
    checkins?: number[]
    contestGames?: string[]
    firstCheckin?: DateRange
    frequencyTypes?: string
    genders?: string
    lastVisit?: DateRange
    points?: number[]
    segmentList?: string[]
    segmentExcludedList?: string[]
    stores?: { id: number; name: string }[]
    surveys?: string[]
    tags?: string
    target?: object
  }>(),
  {
    ages: () => [],
    allContestGames: false,
    allFrequencyTypes: false,
    allGenders: false,
    allSegments: false,
    allStores: false,
    allSurveys: false,
    allTags: false,
    checkins: () => [],
    contestGames: () => [],
    firstCheckin: () => ({}),
    frequencyTypes: '',
    genders: '',
    lastVisit: () => ({}),
    points: () => [],
    segmentList: () => [],
    segmentExcludedList: () => [],
    stores: () => [],
    surveys: () => [],
    tags: '',
    target: () => ({})
  }
)

const { getStoresByPermission } = useStore()

const storesWithPermission = computed(() => getStoresByPermission('campaign.read'))

const lastVisitDate = computed(() => {
  return props.lastVisit.from
    ? t('pages.campaign.campaignTargetBlock.lastVisitFromTo', {
        lastVisit1: props.lastVisit.from,
        lastVisit2: props.lastVisit.to
      })
    : t('pages.campaign.campaignTargetBlock.allVisits')
})

const firstCheckinDate = computed(() => {
  return props.firstCheckin.from
    ? t('pages.campaign.campaignTargetBlock.firstCheckinFromTo', {
        firstCheckin1: props.firstCheckin.from,
        firstCheckin2: props.firstCheckin.to
      })
    : t('pages.campaign.campaignTargetBlock.allCheckins')
})
</script>

<template>
  <div class="flex flex-row gap-2 flex-wrap">
    <span v-if="stores && stores.length">
      <StoreTooltip
        :stores="stores"
        :available-store-ids="storesWithPermission.map((store) => store.id)"
        position="bottom-start"
        detail-expandable
      />
    </span>
    <span v-if="segmentList && segmentList.length" class="gap-2 flex flex-wrap">
      <BaseChip v-if="allSegments" :text="t('pages.campaign.campaignTargetBlock.allSegments')" />

      <BaseChip v-for="segment in segmentList" v-else :key="segment" :text="segment" />
    </span>
    <span v-if="segmentExcludedList && segmentExcludedList.length" class="gap-2 flex flex-wrap">
      <BaseChip
        v-for="segment in segmentExcludedList"
        :key="'excluded-' + segment"
        :text="segment"
        text-classes="line-through"
        color="red"
      />
    </span>
    <span v-if="frequencyTypes">
      <BaseChip :text="frequencyTypes" />
    </span>
    <span>
      <BaseChip v-if="allGenders" :text="t('pages.campaign.campaignTargetBlock.allGenders')" />

      <BaseChip v-else :text="genders" />
    </span>
    <span>
      <BaseChip
        :text="
          ages[0] === ages[1]
            ? t('global.persona.age', { age: ages[0] })
            : t('pages.campaign.campaignTargetBlock.agesFromTo', { age1: ages[0], age2: ages[1] })
        "
      />
    </span>
    <span>
      <BaseChip
        :text="
          checkins[0] === checkins[1]
            ? t('pages.campaign.campaignTargetBlock.checkinExact', { checkin: checkins[0] })
            : t('pages.campaign.campaignTargetBlock.checkinFromTo', { checkin1: checkins[0], checkin2: checkins[1] })
        "
      />
    </span>
    <span>
      <BaseChip
        :text="
          points[0] === points[1]
            ? t('global.persona.points', { points: points[0] })
            : t('pages.campaign.campaignTargetBlock.pointsFromTo', { points1: points[0], points2: points[1] })
        "
      />
    </span>
    <span>
      <BaseChip v-if="allSurveys" :text="t('pages.campaign.campaignTargetBlock.allSurveys')" />
      <BaseChip v-for="(survey, index) in surveys" v-else :key="index" :text="survey" />
    </span>
    <span>
      <BaseChip v-if="allContestGames" :text="t('pages.campaign.campaignTargetBlock.allContestGames')" />
      <BaseChip v-for="(contestGame, index) in contestGames" v-else :key="index" :text="contestGame" />
    </span>
    <span>
      <BaseChip v-if="allTags" :text="t('pages.campaign.campaignTargetBlock.allTags')" />
      <BaseChip v-else :text="tags" />
    </span>
    <span>
      <BaseChip :text="firstCheckinDate" />
    </span>
    <span>
      <BaseChip :text="lastVisitDate" />
    </span>
  </div>
</template>
